import React, { useState, useRef } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import LeaseApiService from '../../services/apiService';
import {
    parseExcelDate, parseExcelNumber, toText, dateColumnFormatter,
    amountColumnFormatter, isEnvelopeFailure,
} from './importHelpers';

// Field order/names taken directly from the backend's SH_PropertyImportRowVM
// (Pro360ERP API Erini/Pro360.Shared.DomainLayer/ViewModels/
// SH_PropertyMasterImportVM.cs), NOT the Excel template's underscored
// headers - that VM's property names are camelCase-on-the-wire PascalCase
// (propertyNo, plotNo, ...), with NO underscores, and only RateSqft/
// ProjectCost are real decimal?/DateTime? - every other field, including the
// ones that look numeric (PlotSize, TotalUnits, TotalParking, Floors,
// BuiltArea, GrossArea, Latitude, Longitude) or date-like (StartDate,
// EndDate, BondDate), is a plain string? because the underlying
// tbProperty_Master columns are varchar, not numeric/date. Sending a JSON
// number for a string? property throws during model binding -> 400, before
// the action even runs; that mismatch was the actual cause of the earlier
// save failure, not a backend bug.
const IMPORT_COLUMN_ORDER = [
    'propertyNo', 'description', 'descriptionBI', 'project', 'location',
    'address1', 'city', 'country', 'subDistrict', 'district',
    'plotNo', 'subPlotNo', 'plotSize', 'communityNo', 'communityName',
    'subCommunityNo', 'subCommunityName', 'contactPerson', 'contactNo', 'rateSqft',
    'developer', 'dContactNo', 'dEmail', 'dContactPerson', 'subDeveloper',
    'sDContactNo', 'sDEmail', 'sDContactPerson', 'agent', 'subAgent',
    'contractor', 'subContractor', 'totalUnits', 'totalParking', 'floors',
    'builtArea', 'grossArea', 'propertyType', 'propertyCategory', 'propertyClass',
    'startDate', 'endDate', 'projectCost', 'projectStatus', 'facilities',
    'remarks', 'escrowBank', 'escrowBankAcc', 'latitude', 'longitude',
    'imageFilename', 'ownerNo', 'ownerName', 'ownerContactNo', 'ownerEmail',
    'referredBy', 'referredOn', 'cardNumber', 'bondNumber', 'bondType',
    'bondDate',
];
// Real decimal? fields on the backend - the only two sent as JSON numbers.
const NUMBER_FIELDS = new Set(['rateSqft', 'projectCost']);
// referredOn is the only real DateTime?; startDate/endDate/bondDate are
// string? (varchar) but still worth parsing as dates for display/Excel
// serial handling - both groups get formatted as plain 'YYYY-MM-DD' text on
// save (matching the backend's own row.ReferredOn?.ToString("yyyy-MM-dd")
// convention for the SP call), never a full ISO timestamp.
const DATE_FIELDS = new Set(['startDate', 'endDate', 'referredOn', 'bondDate']);
const WIDE_FIELDS = new Set(['description', 'location', 'address1', 'facilities', 'remarks']);

const PropertyImport = () => {
    const { t } = useTranslation(['financePosting', 'common']);
    const apiService = new LeaseApiService();
    const fileInputRef = useRef();

    const [importRows, setImportRows] = useState([]);
    const [saving, setSaving] = useState(false);

    const columnDefs = IMPORT_COLUMN_ORDER.map((field) => ({
        headerName: t(`financePosting:propertyColumns.${field}`),
        field,
        filter: true,
        floatingFilter: true,
        suppressMovable: true,
        width: WIDE_FIELDS.has(field) ? 200 : 130,
        ...(DATE_FIELDS.has(field) ? { valueFormatter: dateColumnFormatter } : {}),
        ...(NUMBER_FIELDS.has(field) ? { valueFormatter: amountColumnFormatter } : {}),
    }));

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const data = new Uint8Array(evt.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
                const dataRows = rows.slice(1).filter((row) => row.some((cell) => cell !== '' && cell !== null && cell !== undefined));
                const parsed = dataRows.map((row, index) => {
                    const record = { id: index };
                    IMPORT_COLUMN_ORDER.forEach((field, colIndex) => {
                        const cell = row[colIndex];
                        if (DATE_FIELDS.has(field)) record[field] = parseExcelDate(cell);
                        else if (NUMBER_FIELDS.has(field)) record[field] = parseExcelNumber(cell);
                        else record[field] = toText(cell);
                    });
                    return record;
                });
                setImportRows(parsed);
            } catch (error) {
                console.error('Error parsing Excel file:', error);
                toast.error(t('common:somethingWentWrong', 'Could not read the Excel file'));
            } finally {
                e.target.value = '';
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleRefreshImport = () => {
        setImportRows([]);
    };

    const validateImportRows = () => {
        // Only PropertyNo is actually required server-side (ValidateRows in
        // SH_PropertyMasterService.Save) - matching that exactly rather than
        // being stricter than the API itself.
        for (let i = 0; i < importRows.length; i++) {
            const row = importRows[i];
            const rowNo = i + 1;
            if (!row.propertyNo) {
                toast.warning(t('financePosting:validation.propertyNoRequired', { row: rowNo }));
                return false;
            }
        }
        return true;
    };

    const handleSave = async () => {
        if (!importRows.length) {
            toast.warning(t('financePosting:noRecordsToSave'));
            return;
        }
        if (!validateImportRows()) return;

        try {
            setSaving(true);
            // Body matches SH_PropertyImportSaveVM: { rows: [...] }. Date-like
            // fields are sent as a plain 'YYYY-MM-DD' string, not a full ISO
            // timestamp - startDate/endDate/bondDate are varchar columns on the
            // backend, and even the one real DateTime? (referredOn) gets
            // re-formatted to yyyy-MM-dd server-side before the SP call anyway.
            const body = {
                rows: importRows.map(({ id, ...rest }) => {
                    const out = { ...rest };
                    DATE_FIELDS.forEach((field) => {
                        out[field] = rest[field] ? moment(rest[field]).format('YYYY-MM-DD') : null;
                    });
                    return out;
                }),
            };
            const res = await apiService.post('PROPERTY_IMPORT_SAVE', body);
            // This API family wraps responses as { status, data, message } and can
            // return HTTP 200 even for a business-logic failure - axios not
            // throwing isn't enough on its own, check the envelope's status flag too.
            if (isEnvelopeFailure(res)) {
                toast.error(res?.data?.message || t('common:somethingWentWrong', 'Save failed'));
                return;
            }
            toast.success(res?.data?.message || t('financePosting:recordsSavedSuccessfully'));
            setImportRows([]);
        } catch (error) {
            console.error('Error saving property import rows:', error);
            const serverMessage = error?.response?.data?.message || error?.response?.data?.title;
            toast.error(serverMessage || t('common:somethingWentWrong', 'Save failed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-3">
            <input
                type="file"
                accept=".xlsx,.xls"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
            <div className="border-box2 p-3" style={{ borderRadius: '8px' }}>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="d-flex gap-2">
                        <button className="mainbtn" type="button" onClick={handleImportClick}>
                            {t('financePosting:excelImport')}
                        </button>
                        <button className="mainbtnGry" type="button" onClick={handleRefreshImport}>
                            {t('financePosting:refresh')}
                        </button>
                        {saving ? (
                            <button className="mainbtnSecondary" type="button" disabled>
                                <ClipLoader color="#ffffff" size={18} />
                            </button>
                        ) : (
                            <button className="mainbtnSecondary" type="button" onClick={handleSave} disabled={!importRows.length}>
                                {t('financePosting:save')}
                            </button>
                        )}
                    </div>
                    <div className="text-end">
                        <a href="/templates/Property_Template.xlsx" download="Property_Template.xlsx" className="d-block mb-1">
                            {t('financePosting:downloadTemplate')}
                        </a>
                        <div>
                            <span className="fw-bold me-1">{t('financePosting:totalRecords')}</span>
                            <span>{importRows.length}</span>
                        </div>
                    </div>
                </div>
                <div className="gridshad mt-3">
                    <div className="ag-theme-quartz" style={{ height: 500 }}>
                        <AgGridReact
                            getRowStyle={(params) => (params.node.rowIndex % 2 === 0 ? { background: '#F0EDF5' } : { background: '#fff' })}
                            rowData={importRows}
                            enableFilter={true}
                            floatingFilter={true}
                            columnDefs={columnDefs}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PropertyImport;
